/**
 * NB-37 — the matter's technology area, shown and settable by hand.
 *
 * The area picks the field-specific drafting rules every claim and
 * specification prompt is given. It is detected once from the
 * disclosure and then kept; a value chosen here is kept until the
 * drafter changes it or asks to re-detect, and automatic detection
 * never overwrites it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  getTechnologyArea, patchTechnologyArea, postTechnologyAreaRedetect,
  TechnologyArea,
} from '@/api/client';
import { apiErrorText } from '@/lib/describeApiError';

/** Shown before the server answers, and the order of the list. */
export const TECH_AREA_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'CHEMISTRY', label: 'Chemistry / materials' },
  { value: 'ELECTRONICS', label: 'Electronics' },
  { value: 'MECHANICAL', label: 'Mechanical' },
  { value: 'MEDTECH', label: 'Medical devices' },
  { value: 'SOFTWARE', label: 'Software / computing' },
];

export const TECH_AREA_NOTE =
  'Sets the drafting style for claims and the specification.';

/** The plain name for a stored bucket key ("CHEMISTRY" → "Chemistry / materials"). */
export const techAreaLabel = (bucket?: string | null): string =>
  TECH_AREA_OPTIONS.find((o) => o.value === (bucket || '').toUpperCase())?.label
  || '';

interface Props {
  matterId: string;
  /** Change it to make the control read the area again (e.g. after a
   *  digest run). */
  refreshKey?: unknown;
  /** Called after the drafter set or re-detected the area. */
  onChange?: (area: TechnologyArea) => void;
  disabled?: boolean;
}

const TechAreaSelect: React.FC<Props> = ({
  matterId, refreshKey, onChange, disabled,
}) => {
  const [area, setArea] = useState<TechnologyArea | null>(null);
  const [busy, setBusy] = useState<'' | 'set' | 'redetect'>('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setArea(await getTechnologyArea(matterId));
    } catch {
      // The control still works without the first read.
    }
  }, [matterId]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const apply = async (kind: 'set' | 'redetect', value?: string) => {
    setBusy(kind);
    setError('');
    try {
      const next = kind === 'set'
        ? await patchTechnologyArea(matterId, value as string)
        : await postTechnologyAreaRedetect(matterId);
      setArea(next);
      onChange?.(next);
    } catch (e) {
      setError(apiErrorText(e, kind === 'set'
        ? 'The technology area could not be saved.'
        : 'The technology area could not be detected.'));
    } finally {
      setBusy('');
    }
  };

  const options = area?.options?.length ? area.options : TECH_AREA_OPTIONS;
  const value = area?.tech_bucket || '';

  return (
    <div className="space-y-1" data-testid="tech-area">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor={`tech-area-${matterId}`}
               className="font-medium text-zinc-700">
          Technology area
        </label>
        <select
          id={`tech-area-${matterId}`}
          data-testid="tech-area-select"
          className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm"
          value={value}
          disabled={disabled || busy !== ''}
          onChange={(e) => { if (e.target.value) void apply('set', e.target.value); }}
        >
          {!value && <option value="">Not set</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span className="text-xs text-zinc-500" data-testid="tech-area-source">
          {area?.source === 'drafter' ? 'set by you'
            : area?.source === 'auto' ? 'detected from the disclosure' : ''}
        </span>
        <button
          type="button"
          data-testid="tech-area-redetect"
          className="rounded-md border border-zinc-300 bg-white px-2 py-0.5 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          disabled={disabled || busy !== '' || !area?.can_redetect}
          title={area?.can_redetect
            ? 'Detect the area again from the disclosure digest. Replaces '
              + 'the current choice, including one you set by hand.'
            : 'Read the disclosure first'}
          onClick={() => void apply('redetect')}
        >
          {busy === 'redetect' ? 'Detecting…' : 'Re-detect'}
        </button>
      </div>
      <div className="text-[11px] text-zinc-500">{area?.note || TECH_AREA_NOTE}</div>
      {error && (
        <div className="text-xs text-red-700" role="alert"
             data-testid="tech-area-error">{error}</div>
      )}
    </div>
  );
};

export default TechAreaSelect;
